/**
 * Which sound a radio message triggers.
 *
 * The game has no event bus for audio: a unit says something, and the sound is
 * chosen by reading what it said. That keeps the simulation free of audio, but
 * it makes the wording of every message in `src/sim/` load-bearing. Reword one
 * of the phrases below and its sound goes silent, with nothing to say it has.
 *
 * So the phrases are listed here rather than buried in a regular expression,
 * and `tests/audio.test.ts` reads the simulation source, collects every string
 * passed to `say()`, and fails if any phrase below has stopped appearing in one
 * of them. A phrase that matches nothing is as much a bug as a missing sound.
 */
export type CueName = 'splash' | 'siren' | 'toll' | 'klaxon' | 'blip';

/** Matched case-insensitively, in this order. The first hit wins. */
export const RADIO_CUES: readonly { cue: CueName; phrases: readonly string[] }[] = [
  { cue: 'splash', phrases: ['good drop'] },
  { cue: 'siren', phrases: ['on scene'] },
  { cue: 'toll', phrases: ['crew lost'] },
  {
    cue: 'klaxon',
    phrases: ['pulling out', 'disengaging', 'escape route', 'closing on our position'],
  },
  { cue: 'blip', phrases: ['full tank', 'bucket full'] },
];

/** The first cue a message matches, or null if it triggers no sound of its own. */
export function cueFor(text: string): CueName | null {
  const t = text.toLowerCase();
  return RADIO_CUES.find((c) => c.phrases.some((p) => t.includes(p)))?.cue ?? null;
}
